import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  // ------------TD CHANGE-----------
  // Store the bound reference to ensure add/remove use the same function instance
  #boundEnableButtons = this.#enableButtons.bind(this);

  /**
   * Tracks whether this specific quantity selector disabled itself due to a cart-line update,
   * and stores the original disabled states so we can restore accurately on errors.
   * @type {boolean}
   */
  #didDisableForCartUpdate = false;

  /**
   * The expected `event.detail.sourceId` (cart section id) for the cart update/error that should
   * restore this selector. This scopes restores to the initiating cart instance (drawer vs page).
   * @type {string | null}
   */
  #expectedCartSourceId = null;

  /** @type {{ inputDisabled: boolean; buttonsDisabled: boolean[] } | null} */
  #restoreState = null;

  /** @returns {HTMLButtonElement[]} */
  #getQuantityButtons() {
    const { quantityButtons } = this.refs;
    const buttons = Array.isArray(quantityButtons)
      ? quantityButtons
      : quantityButtons
        ? [quantityButtons]
        : [];

    return buttons.filter((button) => button instanceof HTMLButtonElement);
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for cart completion events to re-enable buttons
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundEnableButtons);
    document.addEventListener(ThemeEvents.cartError, this.#boundEnableButtons);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#boundEnableButtons);
    document.removeEventListener(ThemeEvents.cartError, this.#boundEnableButtons);
  }

  /**
   * Re-enable buttons for this selector *only* when it initiated the cart-line update.
   * Restores the prior disabled state rather than blindly enabling everything.
   * @param {Event} [event]
   */
  #enableButtons(event) {
    if (!this.#didDisableForCartUpdate || !this.#restoreState) return;

    const sourceId = /** @type {any} */ (event)?.detail?.sourceId;
    if (this.#expectedCartSourceId && sourceId && sourceId !== this.#expectedCartSourceId) return;

    const { quantityInput } = this.refs;
    const buttons = this.#getQuantityButtons();

    quantityInput.disabled = this.#restoreState.inputDisabled;
    buttons.forEach((button, index) => {
      button.disabled = this.#restoreState?.buttonsDisabled?.[index] ?? button.disabled;
    });

    // Clear pending restore state after we apply it to avoid future unrelated restores.
    this.#didDisableForCartUpdate = false;
    this.#expectedCartSourceId = null;
    this.#restoreState = null;
  }

  #disableButtons() {
    const { quantityInput } = this.refs;
    const buttons = this.#getQuantityButtons();

    buttons.forEach(button => {
      button.disabled = true;
    });
    quantityInput.disabled = true;
  }
  // ----------END TD CHANGE---------

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

      // ------------TD CHANGE-----------
      // Disable buttons immediately when a change is triggered
      if (quantityInput.dataset.cartLine) {
        // Snapshot current disabled state so we can restore accurately on cart errors.
        this.#didDisableForCartUpdate = true;
        const cartItemsComponent = this.closest('cart-items-component');
        this.#expectedCartSourceId =
          cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId || null : null;
        this.#restoreState = {
          inputDisabled: quantityInput.disabled,
          buttonsDisabled: this.#getQuantityButtons().map((button) => button.disabled),
        };
        this.#disableButtons();
      }
      // ----------END TD CHANGE---------

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;
    const minInt = parseInt(min);
    const maxInt = parseInt(max);
    const newValueInt = parseInt(newValue);

    if (newValueInt < minInt && min) quantityInput.value = min;
    if (newValueInt > maxInt && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
